import classNames from "classnames";
import { useRef, useState } from "react";
import Icon from "./Icon";
import "./ImageCollection.css";
import { useAlbertineTranslation } from "../package/albertine-i18n";
import RemoteImage from "./RemoteImage";

export type Props = {
    images: string[] | undefined;
    isPublic?: boolean;
    /**
     * Number of images to preload at a time. Next batch loads at midpoint.
     * E.g., if preloadCount is 5, initially images 0-4 load.
     * When image 2 becomes visible, images 5-9 start loading.
     * @default 5
     */
    preloadCount?: number;
};

export default function ImageCollection(props: Props) {
    const { images, isPublic, preloadCount = 5 } = props;
    const scrollRef = useRef<HTMLDivElement>(null);
    const t = useAlbertineTranslation();

    const [loadedUpToIndex, setLoadedUpToIndex] = useState(preloadCount);

    function handleImageBecameVisible(index: number) {
        setLoadedUpToIndex((prev) => {
            const triggerThreshold = prev - Math.ceil(preloadCount / 2);
            if (index >= triggerThreshold) {
                return prev + preloadCount;
            }

            return prev;
        });
    }

    const scrollToPrevious = () => {
        scrollRef.current?.scrollTo({
            left:
                (scrollRef.current?.scrollLeft ?? 0) -
                scrollRef.current.clientWidth * (2 / 3),
            behavior: "smooth",
        });
    };

    const scrollToNext = () => {
        scrollRef.current?.scrollTo({
            left:
                (scrollRef.current?.scrollLeft ?? 0) +
                scrollRef.current.clientWidth * (2 / 3),
            behavior: "smooth",
        });
    };

    if (!images || images?.length === 0) return null;

    const showScrollButtons = images.length > 1;
    const singleImage = images.length === 1;
    const imageClassName = classNames(
        "web-lib__image-collection__image",
        singleImage && "web-lib__image-collection__image--single",
    );

    return (
        <div className="web-lib__image-collection">
            {showScrollButtons && (
                <>
                    <button
                        type="button"
                        className="web-lib__image-collection__next"
                        onClick={scrollToNext}
                    >
                        <div className="web-lib__image-collection__chervon-circle">
                            <Icon.ChevronRight.Large.Text />
                        </div>
                    </button>
                    <button
                        type="button"
                        className="web-lib__image-collection__previous"
                        onClick={scrollToPrevious}
                    >
                        <div className="web-lib__image-collection__chervon-circle">
                            <Icon.ChevronLeft.Large.Text />
                        </div>
                    </button>
                </>
            )}

            <div
                ref={scrollRef}
                className={classNames(
                    singleImage
                        ? "web-lib__image-collection__images--single"
                        : "web-lib__image-collection__images",
                    "web-lib__image-collection--scroll-start",
                )}
                onScroll={(e) => {
                    e.currentTarget.classList.toggle(
                        "web-lib__image-collection--scroll-start",
                        e.currentTarget.scrollLeft === 0,
                    );
                    e.currentTarget.classList.toggle(
                        "web-lib__image-collection--scroll-end",
                        e.currentTarget.scrollLeft >=
                            e.currentTarget.scrollWidth - e.currentTarget.clientWidth,
                    );
                }}
            >
                {images.map((image, index) => {
                    if (isPublic) {
                        // We render just the img tags for public templates as they are rendered by the server
                        // and they cannot handle the RemoteImage components as we aren't running the actual React code.
                        return (
                            <img
                                key={image}
                                src={image}
                                className={imageClassName}
                                alt={t("image_collection__booking_image_alt_text", {
                                    index: index + 1,
                                    total: images.length,
                                })}
                            />
                        );
                    }

                    const isSquare = index === 0 && images.length > 2;

                    return (
                        <RemoteImage.Auto
                            key={image}
                            src={image}
                            wrapperClassName={classNames(
                                imageClassName,
                                isSquare && "aspect-square sm-md:aspect-auto",
                            )}
                            imageClassName={classNames(
                                imageClassName,
                                isSquare && "aspect-square sm-md:aspect-auto",
                            )}
                            alt={t("image_collection__booking_image_alt_text", {
                                index: index + 1,
                                total: images.length,
                            })}
                            instantLoad={index < loadedUpToIndex}
                            onBecameVisible={() => handleImageBecameVisible(index)}
                        />
                    );
                })}
            </div>
        </div>
    );
}
